import React from 'react';

import { Button, Checkbox, Form, Input, InputNumber, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';

import { useCreateSubscriptionPlanMutation, useUpdateSubscriptionPlanMutation } from 'features/adminOperations/api';
import { SubscriptionPlan, SubscriptionPlanInput } from 'features/adminOperations/types';

interface PlanModalProps {
  plan?: SubscriptionPlan;
  onClose: () => void;
}

export function PlanModal({ plan, onClose }: PlanModalProps) {
  const { t } = useTranslation();
  const [create, createState] = useCreateSubscriptionPlanMutation();
  const [update, updateState] = useUpdateSubscriptionPlanMutation();

  const handleSave = async (values: SubscriptionPlanInput) => {
    try {
      if (plan) {
        await update({ id: plan.id, body: values }).unwrap();
      } else {
        await create(values).unwrap();
      }
      message.success(t('admin-extensions.saved'));
      onClose();
    } catch {
      message.error(t('admin-extensions.save-error'));
    }
  };

  return (
    <Modal open title={t(plan ? 'admin-extensions.edit-plan' : 'admin-extensions.create-plan')} onCancel={onClose} footer={null} destroyOnClose>
      <Form<SubscriptionPlanInput>
        layout="vertical"
        initialValues={plan ? { ...plan, price: Number(plan.price) } : { lessons: 8, price: 0, 'expires_at_month_end': false }}
        onFinish={(values) => {
          void handleSave(values);
        }}
      >
        <Form.Item name="name" label={t('admin-extensions.name')} rules={[{ required: true, whitespace: true }]}><Input maxLength={256} /></Form.Item>
        <Form.Item name="lessons" label={t('admin-extensions.lessons')} rules={[{ required: true }]}><InputNumber min={1} max={100000} precision={0} /></Form.Item>
        <Form.Item name="price" label={t('admin-extensions.price')} rules={[{ required: true }]}><InputNumber min={0} max={99999999999999} precision={2} /></Form.Item>
        <Form.Item name="expires_at_month_end" valuePropName="checked"><Checkbox>{t('admin-extensions.end-of-month')}</Checkbox></Form.Item>
        <Button type="primary" htmlType="submit" loading={createState.isLoading || updateState.isLoading}>{t('admin-extensions.save')}</Button>
      </Form>
    </Modal>
  );
}
